import "./kiosk.scss";
import { useEffect, useState } from "react";
import { YStack, XStack, Text, Stack, ScrollView, Spinner } from "tamagui";
import { useLocation, useNavigate } from "react-router-dom";
import { useOrder, Product } from "@utils/store/cart/useOrder";
import { formatOrderableGasTitle } from "@utils/func/gasTitle";
import { buildGasCouponDiscountMap, discountedLineTotal } from "@screen/Home/pages/GasShop/utils/couponDiscount";
import { useKioskCheckout } from "./useKioskCheckout";
import { useKiosk } from "./useKiosk";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { appToast } from "@components/Toast";
import { kiosk, floorIntToLabel } from "./kioskTheme";
import KioskIcon from "./KioskIcon";
import { KioskHeader, KioskCTA } from "./components/KioskParts";
import CheckoutGuide, { CheckoutField } from "./components/CheckoutGuide";
import { updateProductItem, delProductItem, delDraftOrder, draftFromItemRes } from "./api/kioskApi";
import { track } from "@lib/tracker";

const KioskCart = () => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const { productList, couponDiscount, draftOrderId, syncDraft, setOrderState, updateQuantity, removeProduct, clearProducts, hideAmountOnPending } = useOrder();
  const xContext = buildXContextHeader(chooseIdentity) || "";

  const { hidePrice, needIdentityCheck, pendingReview } = useKiosk(); // 營業客戶不顯示價格；新客戶/待核對需先做首次加入資料
  // 新客戶/待核對 → 結算前先走「首次加入資料」頁（身分/鋼瓶/配套/合約）；既有客戶直接進結算
  const needJoin = needIdentityCheck || pendingReview;
  // 自動帶最優券 + 算費 + 把折扣寫進 store（讓購物車品項顯示折後瓦斯價，與確認頁一致）
  useKioskCheckout();
  // 優惠券折抵直接折在瓦斯價上（與確認頁訂單摘要一致）
  // couponDiscount === null 代表 preview 尚未回（pending）：品項價顯示 loading，避免先閃原價再跳折後價。
  const discountPending = couponDiscount === null;
  const gasCouponDiscountMap = buildGasCouponDiscountMap(productList ?? [], couponDiscount ?? 0);

  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  // 確認頁「返回」帶 reopenCheckout state → 直接把結帳引導開在指定步驟（例如「怎麼付款？」）
  const location = useLocation();
  const [checkoutStart, setCheckoutStart] = useState<CheckoutField>("time");
  useEffect(() => {
    const reopen = (location.state as { reopenCheckout?: CheckoutField } | null)?.reopenCheckout;
    if (reopen) {
      setCheckoutStart(reopen);
      setShowCheckout(true);
      // 清掉 state，避免重新整理又自動開啟
      navigate(".", { replace: true, state: null });
    }
  }, []);

  useEffect(() => {
    track("funnel_step", { step: "cart" });
  }, []);

  const changeQty = async (p: Product, newQty: number) => {
    if (updatingId || !draftOrderId || !p.line_id) return;
    const type = p.gas_type ? "gas" : "commodity";

    // 樂觀更新
    if (newQty <= 0) removeProduct(p.line_id);
    else updateQuantity(p.line_id, newQty);

    // 草稿已被刪除 / 過期 / 已送出 → BE 回 404 Draft not found
    const isDraftGone = (res: any) =>
      res?.error?.response?.status === 404 || /draft not found/i.test(res?.error?.response?.data?.message || "");
    // 草稿失效：本地購物車已無對應後端草稿，清空並退回首頁重新選購
    const handleGone = () => {
      clearProducts();
      appToast.error("購物車已過期，請重新選購");
      navigate("/kiosk", { replace: true });
    };

    setUpdatingId(p.line_id);
    try {
      if (newQty <= 0) {
        if (productList.length === 1) {
          const res = await delDraftOrder({ draftId: draftOrderId, jwtToken: cusJwtToken || "", xContext });
          // 成功、或草稿本來就不存在 → 都當作已清空
          if (res?.state === "success" || isDraftGone(res)) clearProducts();
        } else {
          const res = await delProductItem({ type, draftId: draftOrderId, lineId: p.line_id, jwtToken: cusJwtToken || "", xContext });
          if (res?.state === "success") {
            const draft = draftFromItemRes(res);
            if (draft) syncDraft(draft);
            else setOrderState("loading");
          } else if (isDraftGone(res)) {
            handleGone();
          }
        }
      } else {
        const body: any = { ...p, qty: newQty };
        delete body.line_id;
        delete body.totalPrice;
        const res = await updateProductItem({ type, draftId: draftOrderId, lineId: p.line_id, body, jwtToken: cusJwtToken || "", xContext });
        if (res?.state === "success") {
          const draft = draftFromItemRes(res);
          if (draft) syncDraft(draft);
          else setOrderState("loading");
        } else if (isDraftGone(res)) {
          handleGone();
        }
      }
    } finally {
      setUpdatingId(null);
    }
  };

  return (
        // 底色沿用全站的淡霧（.gas-skin-v2-bg，定義在 all.scss）而不是複製一份：
    // 兩份定義遲早會走鐘，而客戶在同一個 App 裡會前後看到這兩條流程。
    <YStack flex={1} position="relative" className="gas-skin-v2-bg">
      <KioskHeader title="購物車" onBack={() => navigate("/kiosk")} />

      <ScrollView flex={1} className="kiosk-noscroll kiosk-stagger" contentContainerStyle={{ paddingTop: 6, paddingLeft: 16, paddingRight: 16, paddingBottom: 24 }}>
        <Text fontSize={14} fontWeight="800" color={kiosk.grey} mb={11} ml={4} letterSpacing={0.4}>
          商品
        </Text>

        {productList.length === 0 ? (
          <YStack py={40} ai="center" gap={14}>
            <Text fontSize={15} color="#9aa3b2">
              購物車是空的，回首頁加點瓦斯吧
            </Text>
          </YStack>
        ) : (
          [...productList]
            .sort((a, b) => (a.kilogram ?? Infinity) - (b.kilogram ?? Infinity))
            .map((it, idx) => {
            const name = it.gas_type ? formatOrderableGasTitle(it.kilogram) : it.display_name || "其他商品";
            const sub = [floorIntToLabel(it.floor), it.is_elevator ? "有電梯" : "無電梯", it.usage_location, it.note]
              .filter(Boolean)
              .join(" · ");
            const busy = updatingId === it.line_id;
            // 折扣已攤分到各瓦斯品項（多桶比例攤），顯示原價刪除線 + 折後價，對齊確認訂單頁
            const original = it.unit_price * it.qty;
            const shownPrice = discountedLineTotal(it, idx, gasCouponDiscountMap);
            const hasDiscount = shownPrice < original;
            return (
              <XStack
                key={it.line_id}
                backgroundColor={kiosk.card}
                borderWidth={1}
                borderColor={kiosk.line}
                borderRadius={15}
                p={12}
                mb={11}
                ai="center"
                gap={11}>
                <Stack width={46} height={46} borderRadius={12} ai="center" jc="center" style={{ background: "linear-gradient(160deg,#eaf1ff,#d4e3ff)" }}>
                  <KioskIcon name="cylinder" size={23} color={kiosk.blue} />
                </Stack>
                <YStack flex={1} minWidth={0}>
                  <Text fontSize={16} fontWeight="700" color={kiosk.ink}>
                    {name}
                  </Text>
                  {sub && (
                    <Text fontSize={13} color={kiosk.grey} mt={2} lineHeight={18}>
                      {sub}
                    </Text>
                  )}
                </YStack>
                <YStack ai="flex-end" gap={8}>
                  {/* 瓦斯行隱藏金額或營業客戶時，下訂流程不顯示價格 */}
                  {hideAmountOnPending || hidePrice ? null : discountPending ? (
                    <Stack height={20} jc="center">
                      <Spinner size="small" color={kiosk.blue} />
                    </Stack>
                  ) : (
                    <YStack ai="flex-end">
                      <Text fontSize={16} fontWeight="800" color={hasDiscount ? kiosk.blue : kiosk.ink}>
                        NT${shownPrice.toLocaleString()}
                      </Text>
                      {hasDiscount && (
                        <Text fontSize={12.5} color={kiosk.grey} style={{ textDecorationLine: "line-through" }}>
                          NT${original.toLocaleString()}
                        </Text>
                      )}
                    </YStack>
                  )}
                  <XStack ai="center" borderWidth={1.5} borderColor={kiosk.line} borderRadius={11} overflow="hidden" opacity={busy ? 0.5 : 1}>
                    <Stack
                      width={30}
                      height={30}
                      ai="center"
                      jc="center"
                      backgroundColor="#f4f6fb"
                      cursor="pointer"
                      onPress={() => changeQty(it, it.qty - 1)}>
                      {it.qty <= 1 ? <KioskIcon name="trash" size={16} color={kiosk.danger} /> : <Text fontSize={20} color={kiosk.blue}>−</Text>}
                    </Stack>
                    <Stack minWidth={36} height={30} px={6} ai="center" jc="center" backgroundColor="#fff" position="relative">
                      {busy ? (
                        <Spinner size="small" color={kiosk.blue} />
                      ) : (
                        <Text fontSize={17} fontWeight="800" color={kiosk.ink}>
                          {it.qty}
                        </Text>
                      )}
                    </Stack>
                    <Stack
                      width={30}
                      height={30}
                      ai="center"
                      jc="center"
                      backgroundColor="#f4f6fb"
                      cursor="pointer"
                      onPress={() => changeQty(it, it.qty + 1)}>
                      <Text fontSize={20} color={kiosk.blue}>
                        ＋
                      </Text>
                    </Stack>
                  </XStack>
                </YStack>
              </XStack>
            );
          })
        )}

        <XStack
          backgroundColor="#eef1f6"
          borderRadius={13}
          py={13}
          ai="center"
          jc="center"
          mt={2}
          cursor="pointer"
          onPress={() => navigate("/kiosk")}
          pressStyle={{ opacity: 0.8 }}>
          <Text color="#39435c" fontSize={16} fontWeight="800">
            ＋ 繼續選購
          </Text>
        </XStack>
      </ScrollView>

      <YStack backgroundColor="#fff" borderTopWidth={1} borderColor={kiosk.line} px={16} pt={12} pb={22} style={{ flex: "0 0 auto" }}>
        <KioskCTA
          label="前往結帳"
          disabled={productList.length === 0}
          onPress={() => {
            if (productList.length === 0) {
              appToast.error("購物車是空的");
              return;
            }
            track("funnel_step", { step: "checkout_guide" });
            // 不預寄簡訊（2026-10-14 第三次拍板）：結算頁的驗證卡要客戶勾協議、按「傳送驗證碼」才寄。
            setShowCheckout(true);
          }}
          right={<KioskIcon name="chevron" size={22} color="#fff" />}
        />
      </YStack>

      {showCheckout && (
        <CheckoutGuide fromConfirm={false} startField={checkoutStart} onClose={() => setShowCheckout(false)} onDone={() => navigate(needJoin ? "/kiosk/join" : "/kiosk/confirm")} />
      )}
    </YStack>
  );
};

export default KioskCart;
